import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    appLocale: String,
    hasExistingInfo: Boolean,
  };

  static targets = [
    "linkedinInput",
    "cvInput",
    "profileLink",
    "linkedinVerification",
    "initialInstructions",
    "submitButton",
    "fillMannuallyBtn",
  ];

  connect() {
    this.updateSubmitState();
    this.checkJobStatus();
  }

  // ================================
  // HELPERS
  // ================================

  updateSubmitState() {
    const linkedinValue = this.hasLinkedinValue();
    const linkedinValid =
      !linkedinValue || this.isValidLinkedin(this.linkedinInputTarget.value);

    const hasCv = this.hasCvFile();

    const shouldEnable = (linkedinValue && linkedinValid) || hasCv;

    this.submitButtonTarget.disabled = !shouldEnable;
    this.submitButtonTarget.style.cursor = shouldEnable
      ? "pointer"
      : "not-allowed";
  }

  // ================================
  // LINKEDIN
  // ================================

  handleLinkedinChange(event) {
    const input = event.target;
    const value = input.value.trim();

    if (!value) {
      this.clearLinkedinError(input);
    } else if (!this.isValidLinkedin(value)) {
      this.showLinkedinError(input, "Ex: https://www.linkedin.com/in/seu-nome");
    } else {
      this.clearLinkedinError(input);
      this.updateProfileLink(value);
    }

    this.updateSubmitState();
  }

  isValidLinkedin(value) {
    let url = value.trim();
    if (!url) return true;

    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;

    try {
      const uri = new URL(url);

      if (!["http:", "https:"].includes(uri.protocol)) return false;

      if (!["linkedin.com", "www.linkedin.com"].includes(uri.hostname))
        return false;

      if (!/^\/in\/[^\/\s]+\/?$/i.test(uri.pathname)) return false;

      return !(uri.search || uri.hash);
    } catch {
      return false;
    }
  }

  hasLinkedinValue() {
    if (!this.hasLinkedinInputTarget) return false;
    return this.linkedinInputTarget.value.trim() !== "";
  }

  // ================================
  // CV
  // ================================

  handleCvChange(event) {
    this.updateSubmitState();

    const autosubmit = event.params?.autosubmit;
    if (autosubmit) {
      if (this.hasExistingInfoValue) {
        const confirmMessages = {
          en: "You already have a CV registered. If you upload a new one, it will replace existing information.",
          "pt-BR": "Você já tem um CV cadastrado, se cadastrar um novo, substituirá as informações existentes."
        };
        const message = confirmMessages[this.appLocaleValue] || confirmMessages["pt-BR"];
        const confirmed = window.confirm(message);
        if (!confirmed) {
          this.clearCvInput();
          this.updateSubmitState();
          return;
        }
        this.ensureReplaceExistingInput();
      }
      this.handleOnboardingParseProfileBtnClick();
    }
  }

  clearCvInput() {
    if (this.hasCvInputTarget) {
      this.cvInputTarget.value = "";
    }
    const fileUploadBox = this.element.querySelector('[data-controller="file-upload"]');
    if (fileUploadBox) {
      const selectedFile = fileUploadBox.querySelector('[data-file-upload-target="selectedFile"]');
      const fileStatus = fileUploadBox.querySelector('[data-file-upload-target="fileStatus"]');
      if (selectedFile) selectedFile.classList.add("d-none");
      if (fileStatus) fileStatus.classList.remove("d-none");
    }
  }

  ensureReplaceExistingInput() {
    let input = this.element.querySelector('input[name="replace_existing_info"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "replace_existing_info";
      this.element.appendChild(input);
    }
    input.value = "true";
  }

  handleDrop() {
    setTimeout(() => {
      this.updateSubmitState();
    }, 100);
  }

  updateCvRequirements() {
    const hasCv = this.hasCvFile();

    if (hasCv) {
      this.submitButtonTarget.disabled = false;
      this.removeRequired(this.linkedinInputTarget);
      this.clearLinkedinError(this.linkedinInputTarget);
    } else if (!this.hasLinkedinValue()) {
      this.submitButtonTarget.disabled = true;
      this.addRequired(this.linkedinInputTarget);
    }
  }

  hasCvFile() {
    return this.cvInputTarget?.files?.length;
  }

  // ================================
  // INITIAL STATE
  // ================================

  updateRequiredStates() {
    if (!this.hasLinkedinValue() && !this.hasCvFile()) {
      if (this.cvInputTarget) {
        this.addRequired(this.cvInputTarget);
      }
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.style.cursor = "not-allowed";
    } else {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.style.cursor = "pointer";
    }
  }

  // ================================
  // ERROR HANDLING
  // ================================

  showLinkedinError(input, message) {
    input.classList.add("is-invalid");
    input.setCustomValidity(message);

    let error = input.parentNode.querySelector(".linkedin-error");

    if (!error) {
      error = document.createElement("div");
      error.classList.add("invalid-feedback", "linkedin-error");
      input.parentNode.appendChild(error);
    }

    error.textContent = message;
    error.style.display = "block";
  }

  clearLinkedinError(input) {
    input.classList.remove("is-invalid");
    input.setCustomValidity("");

    const error = input.parentNode.querySelector(".linkedin-error");
    if (error) error.remove();
  }

  // ================================
  // PROFILE LINK PREVIEW
  // ================================

  updateProfileLink(url) {
    let formattedUrl = url.trim();

    if (!formattedUrl.startsWith("http")) {
      formattedUrl = `https://${formattedUrl}`;
    }

    if (this.hasProfileLinkTarget) {
      this.profileLinkTargets.forEach((link) => {
        link.href = formattedUrl;
      });
    }

    if (
      this.hasLinkedinVerificationTarget &&
      this.hasInitialInstructionsTarget
    ) {
      this.linkedinVerificationTarget.classList.remove("d-none");
      this.initialInstructionsTarget.classList.add("d-none");
    }
  }

  // ================================
  // REQUIRED MANIPULATION
  // ================================

  removeRequired(element) {
    if (!element) return;

    element.removeAttribute("required");

    const wrapper = element.closest(".form-group");
    if (wrapper) {
      wrapper.classList.remove("required");

      wrapper.querySelectorAll("label").forEach((label) => {
        label.classList.remove("required", "form-label-required");
      });

      const abbr = wrapper.querySelector('abbr[title="required"]');
      if (abbr) abbr.remove();
    }
  }

  addRequired(element) {
    if (!element) return;

    element.setAttribute("required", "required");

    const wrapper = element.closest(".form-group");
    if (wrapper) {
      wrapper.classList.add("required");

      wrapper.querySelectorAll("label").forEach((label) => {
        label.classList.add("required", "form-label-required");

        if (!label.querySelector("abbr")) {
          const abbr = document.createElement("abbr");
          abbr.title = "required";
          abbr.textContent = " *";
          label.appendChild(abbr);
        }
      });
    }
  }

  // ================================
  // JOB STATUS POLLING
  // ================================

  checkJobStatus() {
    if (!this.hasPendingElement) return;

    const checkStatus = () => {
      fetch(
        `/ats/${this.organizationParam}/candidates/onboarding/${this.onboardingId}/status`
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            window.location.reload();
          } else if (!data.completed) {
            setTimeout(checkStatus, 2000);
          } else {
            window.location = data.redirect_url;
          }
        });
    };

    setTimeout(checkStatus, 2000);
  }

  handleOnboardingParseProfileBtnClick() {
    const submitBtn = document.querySelector("#form-navigation-hidden-btn");
    const fillMannuallyBtn = this.fillMannuallyBtnTarget;

    if (fillMannuallyBtn) {
      const messages = {
        en: "Uploading...",
        "pt-BR": "Enviando...",
      };
      const message = messages[this.appLocaleValue] || messages["pt-BR"];
      fillMannuallyBtn.classList.add("disabled");
      fillMannuallyBtn.setAttribute("aria-disabled", "true");
      fillMannuallyBtn.style.pointerEvents = "none";
      fillMannuallyBtn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${message}`;
    }

    submitBtn.click();
  }
}
